'use-strict';

import { IScope } from 'angular';
import { Constants, SettingsAPIService } from '../../../shared/api/settings-api.service';
import { ConfigService } from '../../../shared/config/config.service';
import { InterfaceService } from '../../../shared/interface/interface.service';
import { EExternalAPI } from '../../../src/app/@shared-types/api';
import { LinkEntryAPIConfig } from '../../../src/app/@shared-types/api/stellantis';
import LogService from '../../../src/app/shared/services/log';
import { ToastService, ToastType } from '../../../ui-components/toast/toast.service';
import { EConstantType } from '../../../src/app/@shared-types/config/enums';

interface MissingVehicle {
  vehicleId: string,
  orderNumber: string,
  status: 'MISSING',
  vin?: string,
}

/* @ngInject */
export default class LinkDashboardController {
  private $scope: IScope;

  public linkEntryAPIConfig: LinkEntryAPIConfig | null = null;

  public importing = false;

  private settingsApiService: SettingsAPIService;

  private interfaceService: InterfaceService;

  private toastService: ToastService;

  private logService: LogService;

  public locations: { [key: string]: string } | null;

  public saving = false;

  public notificationAdmins: string | null = null;

  public missingVehicles: MissingVehicle[] | null = null;

  public vehicleTypes: { [key: string]: string } | null;

  public multiAccounts: { [key: number]: boolean } = {};

  constructor(
    $scope: IScope,
    settingsApiService: SettingsAPIService,
    interfaceService: InterfaceService,
    toastService: ToastService,
    configService: ConfigService,
    logService: LogService,
  ) {
    this.settingsApiService = settingsApiService;
    this.interfaceService = interfaceService;
    this.$scope = $scope;
    this.toastService = toastService;
    this.logService = logService;

    this.getLinkEntryConfig();
    this.getAdmins();
    this.getMissingVehicles();
    this.locations = configService.get('general_locations');
    this.vehicleTypes = configService.get('sales_car_types');
  }

  private getLinkEntryConfig = async (): Promise<void> => {
    const configs = await this.settingsApiService
      .getAPIConfig<LinkEntryAPIConfig[]>(EExternalAPI.LINK_ENTRY).catch();

    [this.linkEntryAPIConfig] = configs;

    this.linkEntryAPIConfig.accounts.forEach((account, index) => {
      if (account.multiAccountUsernames) this.multiAccounts[index] = true;
    });

    this.interfaceService.applyScope(this.$scope);
  };

  private getMissingVehicles = async (): Promise<void> => {
    const constants = await this.settingsApiService.getAllConstants();
    const missingVehicles = constants.find((c) => c.attribute === 'link_entry_api_missing_cars')?.value as MissingVehicle[] || null;
    this.missingVehicles = missingVehicles;
  };

  private getAdmins = async () => {
    this.settingsApiService.getAllConstants()
      .then((constants) => {
        const admins = (constants as Constants[]).find((c) => c.attribute === 'link_entry_api_missing_cars_admins');
        if (!admins) {
          this.notificationAdmins = '';
        } else {
          this.notificationAdmins = (admins.value as number[]).join(',');
        }
      })
      .catch((e) => this.logService.error(e));
  };

  public saveAdmins = (): void => {
    this.settingsApiService.updateConstants([{
      key: 'link_entry_api_missing_cars_admins',
      value: this.notificationAdmins?.split(',').map((a) => parseInt(a, 10)) || [],
    }])
      .then(() => {
        this.toastService.toast(ToastType.SUCCESS, 'Benutzer gespeichert.');
      }).catch((e) => {
        this.toastService.toast(ToastType.ERROR, 'Fehler beim Speichern der Benutzer.');
        this.logService.error(e);
      });
  };

  public updateConfig = (
    config: unknown,
  ): Promise<void> => this.settingsApiService.updateAPIConfig(EExternalAPI.LINK_ENTRY, config)
    .then(() => this.toastService.toast(ToastType.SUCCESS, 'Einstellungen gespeichert.'));

  public startImport = (): void => {
    this.importing = true;
    this.settingsApiService.startLinkEntryImport()
      .then(() => {
        this.importing = false;
        this.getLinkEntryConfig();
        this.toastService.toast(ToastType.SUCCESS, 'Import von Link Entry Fahrzeugen durchgeführt.');
      }).catch((e) => {
        this.importing = false;
        this.toastService.toast(ToastType.ERROR, 'Fehler beim manuellen Import von Link Entry.');
        this.logService.error(e);
      });
  };

  public removeVehicleFromMissingList = (vehicleId: string): void => {
    if (!this.missingVehicles) return;
    this.missingVehicles = this.missingVehicles.filter((v) => v.vehicleId !== vehicleId);
  };

  public saveMissingVehicles = async (): Promise<void> => {
    if (!this.missingVehicles) return;

    this.saving = true;

    await this.settingsApiService.updateConstants([{
      key: EConstantType.LINK_ENTRY_MISSING_VEHICLES,
      value: this.missingVehicles,
    }]).then(() => {
      this.toastService.toast(ToastType.SUCCESS, 'Liste fehlender Fahrzeuge gespeichert.');
    }).catch((e) => {
      this.toastService.toast(ToastType.ERROR, 'Fehler Speichern.');
      this.logService.error(e);
    });

    this.saving = false;
  };
}
